import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type WorkItemTypeItem = { id: string; name: string };

export function QCRecordFilter() {
  const nav = useNavigate({ from: "/app/qc/records" });
  const search = useSearch({ from: "/app/qc/records" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const searchFilter = findCondition(filters, "search");
  const overallStatusFilter = findCondition(filters, "overallStatus");
  const workItemTypeFilter = findCondition(filters, "workItemTypeId");

  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((searchFilter?.value as string) || ""),
    [searchFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "search",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "search");
    }
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const overallStatusValue = (overallStatusFilter?.value as string) || "";
  const workItemTypeValue = (workItemTypeFilter?.value as string) || "";

  const hasFilter =
    !!searchFilter?.value || !!overallStatusValue || !!workItemTypeValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="record-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ (ຫມາຍເຫດ / ສະຖານທີ່)..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <InfiniteCombobox<WorkItemTypeItem>
            queryKey={["qc-work-item-types", "record-filter"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/qc/work-item-types/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/qc/work-item-types/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={workItemTypeValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "workItemTypeId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "workItemTypeId");
              }
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກປະເພດງານ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <SimpleSelect
            value={overallStatusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "overallStatus");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "overallStatus",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "pass", label: "ຜ່ານ" },
              { value: "fail", label: "ບໍ່ຜ່ານ" },
              { value: "pending", label: "ກຳລັງກວດສອບ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                  sort: search.sort,
                  filters: undefined,
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
